import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { genreTags } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";
import DramaCard from "@/components/feature/DramaCard";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";

const wrap = "mx-auto w-full max-w-[1280px] px-4 md:px-6";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialQuery = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQuery);
  const [submitted, setSubmitted] = useState(initialQuery);

  usePageMeta(
    submitted ? `搜索「${submitted}」- 泰剧腐剧双男主` : "搜索 - 泰剧腐剧双男主",
    "输入剧名、演员或题材关键词，快速找到你感兴趣的泰国双男主剧集。",
  );

  const results = useMemo(() => {
    const q = submitted.trim().toLowerCase();
    if (!q) return [];
    return dramas.filter((d) => {
      const haystack = [d.title, d.subtitle, d.category, d.synopsis, d.shortIntro, ...d.cast, ...d.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [submitted]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSubmitted(q);
    setParams(q ? { q } : {});
  };

  const hotKeywords = ["黑帮", "青春校园", "甜宠", "虐恋", "治愈", "KinnPorsche", "Ohm"];

  return (
    <div className={wrap}>
      <div className="border-b border-background-200 pt-6 pb-5">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
        <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
          搜索剧集
        </h1>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入剧名、演员或题材关键词，如「黑帮」「甜宠」"
              className="w-full bg-transparent py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-search-line"></i>搜索
          </button>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">热门搜索：</span>
          {hotKeywords.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setKeyword(k);
                setSubmitted(k);
                setParams({ q: k });
              }}
              className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
            >
              {k}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 pb-4">
        {submitted ? (
          <>
            <p className="text-sm text-foreground-500">
              关键词「<span className="font-bold text-primary-600">{submitted}</span>」共找到{" "}
              <span className="font-bold text-primary-600">{results.length}</span> 部相关作品
            </p>
            {results.length > 0 ? (
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {results.map((drama, i) => (
                  <DramaCard key={drama.id} drama={drama} index={i} />
                ))}
              </div>
            ) : (
              <div className="mt-5">
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有找到相关作品"
                  description="换个关键词试试，或从下方的热门标签里挑一个你感兴趣的题材。"
                />
                <div className="mt-6">
                  <h3 className="font-heading text-base font-bold text-foreground-900">猜你想找</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {genreTags.slice(0, 8).map((t) => (
                      <Link
                        key={t.name}
                        to={`/tags/${t.name}`}
                        className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                      >
                        {t.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="mt-5">
            <EmptyState
              icon="ri-search-2-line"
              title="开始搜索你想看的剧集"
              description="输入剧名、演员或题材关键词，即可快速定位相关作品。"
            />
          </div>
        )}
      </div>
    </div>
  );
}